import { useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import {
  initializeFirebase,
  onAuthChanged,
  signInAnonymously as firebaseSignInAnonymously,
  signInWithGoogle as firebaseSignInWithGoogle,
  signOut as firebaseSignOut,
  setDisplayName as firebaseSetDisplayName,
  getIdToken,
  formatDisplayName,
  handleGoogleRedirectResult,
} from '../services/firebase/config';

/**
 * Hook to manage Firebase authentication with Zustand store
 */
export function useAuth() {
  const { user, isLoading, error, token, setUser, setToken, setLoading, setError, signOut: clearStore } = useAuthStore();

  // Initialize Firebase and subscribe to auth state changes
  useEffect(() => {
    initializeFirebase();
    
    let unsubscribe: (() => void) | undefined;
    
    async function initAuth() {
      // Handle redirect result from Google Sign-In (non-iOS/Safari browsers)
      // iOS/Safari use popup flow so handleGoogleRedirectResult returns null there
      try {
        const redirectUser = await handleGoogleRedirectResult();
        if (redirectUser) {
          // Redirect sign-in was successful - user will be available via onAuthStateChanged
          // eslint-disable-next-line no-console
          console.log('[Auth] Redirect sign-in successful:', redirectUser.displayName);
        }
      } catch (err) {
        console.error('[Auth] Google redirect result error:', err);
        // Only show error if it's a real error (not cancelled/missing state)
        if (err instanceof Error && !err.message.includes('cancelled')) {
          setError(err.message);
        }
      }
      
      // Now set up ongoing auth state listener
      unsubscribe = onAuthChanged(async (firebaseUser) => {
        if (firebaseUser) {
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isAnonymous: firebaseUser.isAnonymous,
          });
          
          // Get and store the ID token
          const idToken = await firebaseUser.getIdToken();
          setToken(idToken);
        } else {
          setUser(null);
          setToken(null);
        }
        setLoading(false);
      });
    }
    
    initAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [setUser, setToken, setLoading, setError]);

  /**
   * Sign in anonymously with optional display name
   */
  const signInAnonymously = useCallback(async (displayName?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      await firebaseSignInAnonymously(displayName);
      // Auth state listener will handle updating the store
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
      throw err;
    }
  }, [setLoading, setError]);

  /**
   * Sign in with Google
   */
  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await firebaseSignInWithGoogle();
      // Auth state listener will handle updating the store
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in with Google';
      setError(message);
      throw err;
    }
  }, [setLoading, setError]);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await firebaseSignOut();
      clearStore();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign out';
      setError(message);
      throw err;
    }
  }, [setLoading, setError, clearStore]);

  /**
   * Update display name
   */
  const updateDisplayName = useCallback(async (displayName: string) => {
    if (!user) throw new Error('No user signed in');
    
    try {
      await firebaseSetDisplayName(displayName);
      setUser({
        ...user,
        displayName,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update display name';
      setError(message);
      throw err;
    }
  }, [user, setUser, setError]);

  /**
   * Get fresh ID token (for API calls)
   */
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const newToken = await getIdToken();
    if (newToken) {
      setToken(newToken);
    }
    return newToken;
  }, [setToken]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAnonymous: user?.isAnonymous ?? false,
    isGuest: user?.isAnonymous ?? false,
    error,
    token,
    displayName: user ? formatDisplayName(user.displayName) : null,
    
    // Actions
    signInAnonymously,
    signInWithGoogle,
    signOut,
    updateDisplayName,
    refreshToken,
  };
}
